import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SectionProps } from "./context";
import { Band, Head, cn, rv } from "./ui";
import { api, apiErrorText } from "@/lib/api";
import { ROLE_LABELS, useAuth, type Role } from "./auth";
import { goAnchor } from "./app-nav";
import { contactsApi, type Contact } from "./contacts-api";
import { companiesApi, showCompany, type Company } from "./companies-api";
import { showContact } from "./contacts-api";
import { tagColors } from "./tags-meta";
import { NotesBlock, TasksBlock } from "./activity";
import { DocRow } from "./finance";
import { describeAds } from "./ads";
import { dealsApi, money, takeDeal, takeNewDeal, type Deal, type DealInput, type DealStage, type Pipeline, type StageKind } from "./deals-api";
import { locale, t, tn } from "@/lib/i18n";

/* «Сделки» (этап 5): канбан по этапам воронки, карточка сделки с историей этапов, списки выигранных и проигранных.
   Сделки и воронки приходят из Zoho CRM синхронизацией (раздел «Zoho»), новые заводятся здесь; агент видит свои.
   Этапы воронки меняют владелец и администраторы («Этапы» в шапке). */

const input =
  "h-9 w-full rounded-btn border border-border bg-background px-2.5 text-[13px] text-foreground outline-none transition focus:border-primary disabled:opacity-60";
const btn = "inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn border border-border bg-background px-3 text-[12.5px] font-semibold text-foreground transition hover:bg-muted disabled:cursor-default disabled:opacity-50";
const primary = "inline-flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-btn bg-primary px-3 text-[12.5px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:cursor-default disabled:opacity-50";

const hex = (color: string) => tagColors.find((c) => c.id === color)?.hex ?? tagColors[0].hex;
const day = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(locale) : "—");
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" }) : "—");
const KIND_LABEL: Record<StageKind, string> = { open: t("в работе"), won: t("выиграна"), lost: t("проиграна") };
const days = (n: number) => tn(n, "день", "дня", "дней");

function useToast() {
  const [toast, setToast] = useState("");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback((text: string) => {
    setToast(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setToast(""), 4600);
  }, []);
  return { toast, flash };
}

type Owner = { id: string; name: string; role: string };

function DealsDemo({ title, subtitle }: SectionProps) {
  const { user } = useAuth();
  const canEditStages = user?.role === "owner" || user?.role === "admin";
  const staff = user?.role === "owner" || user?.role === "admin" || user?.role === "manager" || user?.role === "viewer";
  const { toast, flash } = useToast();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [pipelineId, setPipelineId] = useState("");
  const [openId, setOpenId] = useState<string | null>(() => takeDeal());
  const [draft, setDraft] = useState<DealInput | null>(() => takeNewDeal());
  const [stagesOpen, setStagesOpen] = useState(false);
  const [owners, setOwners] = useState<Owner[]>([]);

  const loadPipelines = useCallback(
    () =>
      dealsApi.pipelines().then(
        (rows) => {
          setPipelines(rows);
          setPipelineId((cur) => (rows.some((p) => p.id === cur) ? cur : (rows.find((p) => p.isDefault) ?? rows[0])?.id ?? ""));
        },
        (e) => flash(apiErrorText(e)),
      ),
    [flash],
  );
  useEffect(() => {
    void loadPipelines();
  }, [loadPipelines]);
  useEffect(() => {
    if (!user) return;
    if (!staff) {
      setOwners([{ id: user.id, name: user.name || user.email, role: ROLE_LABELS[user.role] }]);
      return;
    }
    api<{ id: string; name: string; email: string; role: Role; status: string }[]>("/users")
      .then((rows) => setOwners(rows.filter((r) => r.status !== "disabled").map((r) => ({ id: r.id, name: r.name || r.email, role: ROLE_LABELS[r.role] ?? r.role }))))
      .catch(() => setOwners([{ id: user.id, name: user.name || user.email, role: ROLE_LABELS[user.role] }]));
  }, [user, staff]);

  const pipeline = pipelines.find((p) => p.id === pipelineId) ?? null;

  return (
    <Band id="deals" tone="muted">
      <Head title={title} subtitle={subtitle} />
      <div {...rv(1)} className="mt-6 overflow-hidden rounded-card border border-border bg-card shadow-sm">
        {openId ? (
          <DealCard id={openId} pipelines={pipelines} owners={owners} staff={staff} onBack={() => setOpenId(null)} onChanged={loadPipelines} flash={flash} />
        ) : draft ? (
          <NewDeal draft={draft} pipelines={pipelines} pipelineId={pipelineId} onCancel={() => setDraft(null)} onCreated={(d) => { setDraft(null); setOpenId(d.id); void loadPipelines(); }} flash={flash} />
        ) : stagesOpen && pipeline ? (
          <StageEditor pipeline={pipeline} pipelines={pipelines} onClose={() => setStagesOpen(false)} onChanged={loadPipelines} flash={flash} />
        ) : (
          <Board
            pipelines={pipelines}
            pipelineId={pipelineId}
            onPipeline={setPipelineId}
            canEditStages={canEditStages}
            canCreate={user?.role !== "viewer"}
            onStages={() => setStagesOpen(true)}
            onNew={() => setDraft({ pipelineId })}
            onOpen={setOpenId}
            flash={flash}
          />
        )}
      </div>
      {toast ? (
        <div role="status" className="fixed bottom-5 start-1/2 z-50 -translate-x-1/2 rounded-btn bg-foreground px-4 py-2.5 text-[13px] text-background shadow-lg">
          {toast}
        </div>
      ) : null}
    </Band>
  );
}

/* ---------------------------------------------------------------- канбан и списки */

type View = "board" | "won" | "lost";

function Board({
  pipelines,
  pipelineId,
  onPipeline,
  canEditStages,
  canCreate,
  onStages,
  onNew,
  onOpen,
  flash,
}: {
  pipelines: Pipeline[];
  pipelineId: string;
  onPipeline: (id: string) => void;
  canEditStages: boolean;
  canCreate: boolean;
  onStages: () => void;
  onNew: () => void;
  onOpen: (id: string) => void;
  flash: (t: string) => void;
}) {
  const [view, setView] = useState<View>("board");
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState("");
  const [board, setBoard] = useState<Awaited<ReturnType<typeof dealsApi.board>> | null>(null);
  const [closed, setClosed] = useState<{ items: Deal[]; total: number } | null>(null);

  useEffect(() => {
    if (!pipelineId) return;
    const h = setTimeout(() => {
      if (view === "board") {
        dealsApi.board({ pipeline: pipelineId, owner: mine ? "me" : "", q, limit: 40 }).then(setBoard, (e) => flash(apiErrorText(e)));
      } else {
        dealsApi.list({ pipeline: pipelineId, status: view, owner: mine ? "me" : "", q, size: 100 }).then(setClosed, (e) => flash(apiErrorText(e)));
      }
    }, 250);
    return () => clearTimeout(h);
  }, [pipelineId, view, mine, q, flash]);

  const open = board?.stages.filter((s) => s.kind === "open") ?? [];
  const totalOpen = open.reduce((a, s) => a + s.count, 0);
  const sumOpen = open.reduce((a, s) => a + s.sum, 0);

  return (
    <div className="p-3.5 md:p-5">
      <div className="flex flex-wrap items-center gap-2">
        {pipelines.length > 1 ? (
          <select value={pipelineId} onChange={(e) => onPipeline(e.target.value)} aria-label={t("Воронка")} className={cn(input, "w-auto")}>
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        ) : null}
        <div className="flex flex-wrap gap-1.5">
          {(
            [
              ["board", t("В работе")],
              ["won", t("Выиграны")],
              ["lost", t("Проиграны")],
            ] as [View, string][]
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setView(id)}
              className={cn("h-8 cursor-pointer rounded-full border px-3 text-[12px] font-semibold transition", view === id ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:text-foreground")}
            >
              {label}
            </button>
          ))}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1.5 text-[12.5px] text-muted-foreground">
          <input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} className="accent-primary" />
          {t("Только мои")}
        </label>
        <div className="relative min-w-[200px] flex-1">
          <i className="ri-search-line pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Сделка, клиент, компания, продавец")} aria-label={t("Поиск сделок")} className={cn(input, "ps-8")} />
        </div>
        {canEditStages ? (
          <button type="button" onClick={onStages} className={btn}>
            <i className="ri-settings-3-line" aria-hidden />
            {t("Этапы")}
          </button>
        ) : null}
        {canCreate ? (
          <button type="button" onClick={onNew} className={primary}>
            <i className="ri-add-line" aria-hidden />
            {t("Сделка")}
          </button>
        ) : null}
      </div>

      {view === "board" ? (
        <>
          <p className="mt-3 text-[12px] text-muted-foreground">
            {board ? t("{n} в работе на {sum}", { n: totalOpen.toLocaleString(locale), sum: money(sumOpen) }) : t("Загрузка…")} · {t("карточка показывает дни на этапе; сумма — у сделок с указанной суммой")}
          </p>
          <div className="mt-2 flex gap-3 overflow-x-auto pb-2">
            {open.map((col) => (
              <section key={col.id} aria-label={col.name} className="flex w-64 flex-none flex-col rounded-btn border border-border bg-background/60">
                <header className="flex items-center gap-2 border-b border-border px-3 py-2">
                  <span className="size-2.5 flex-none rounded-full" style={{ background: hex(col.color) }} aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold">{col.name}</span>
                  <span className="text-[11.5px] tabular-nums text-muted-foreground">{col.count}</span>
                </header>
                <p className="px-3 pt-1.5 text-[11px] tabular-nums text-muted-foreground">{col.sum ? money(col.sum) : t("без суммы")}</p>
                <ul className="flex max-h-[60vh] flex-col gap-1.5 overflow-y-auto p-2">
                  {col.items.map((d) => (
                    <li key={d.id}>
                      <button type="button" onClick={() => onOpen(d.id)} className="w-full cursor-pointer rounded-btn border border-border bg-card px-2.5 py-2 text-start shadow-sm transition hover:border-primary">
                        <span className="block truncate text-[12.5px] font-semibold">{d.title}</span>
                        <span className="block truncate text-[11.5px] text-muted-foreground">{d.company?.name || d.contact?.name || "—"}</span>
                        <span className="mt-1 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                          <span className="tabular-nums">{d.amount !== null ? money(d.amount, d.currency) : ""}</span>
                          <span className={cn(d.daysInStage > 14 && "text-amber-600")}>{days(d.daysInStage)}</span>
                        </span>
                        <span className="block truncate text-[11px] text-muted-foreground">{d.owner || d.salesperson}</span>
                      </button>
                    </li>
                  ))}
                  {col.count > col.items.length ? <li className="px-1 py-1 text-[11px] text-muted-foreground">{t("ещё {n} — уточните поиск", { n: col.count - col.items.length })}</li> : null}
                  {col.count === 0 ? <li className="px-1 py-2 text-center text-[11.5px] text-muted-foreground">{t("пусто")}</li> : null}
                </ul>
              </section>
            ))}
            {board && open.length === 0 ? <p className="px-2 py-6 text-[12.5px] text-muted-foreground">{t("В воронке нет этапов «в работе» — добавьте их в «Этапы».")}</p> : null}
          </div>
        </>
      ) : (
        <>
          <p className="mt-3 text-[12px] text-muted-foreground">{closed ? t("{n} сделок", { n: closed.total.toLocaleString(locale) }) : t("Загрузка…")}</p>
          <div className="mt-2 overflow-x-auto rounded-btn border border-border">
            <table className="w-full min-w-[720px] border-collapse text-start">
              <thead>
                <tr className="border-b border-border bg-background/60">
                  {[t("Сделка"), t("Клиент"), t("Этап"), t("Сумма"), t("Ответственный"), t("Закрыта")].map((h, i) => (
                    <th key={h} scope="col" className={cn("px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground", i === 3 ? "text-end" : "text-start")}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(closed?.items ?? []).map((d) => (
                  <tr key={d.id} onClick={() => onOpen(d.id)} className="cursor-pointer border-b border-border/60 transition last:border-0 hover:bg-muted/60">
                    <td className="max-w-[18rem] truncate px-3 py-2 text-[12.5px] font-semibold">{d.title}</td>
                    <td className="max-w-[14rem] truncate px-3 py-2 text-[12px] text-muted-foreground">{d.company?.name || d.contact?.name || "—"}</td>
                    <td className="px-3 py-2 text-[12px]">
                      <StageChip stage={d.stage} />
                    </td>
                    <td className="px-3 py-2 text-end text-[12px] tabular-nums">{d.amount !== null ? money(d.amount, d.currency) : "—"}</td>
                    <td className="max-w-[10rem] truncate px-3 py-2 text-[12px] text-muted-foreground">{d.owner || d.salesperson || "—"}</td>
                    <td className="px-3 py-2 text-[12px] text-muted-foreground">{day(d.closedAt)}</td>
                  </tr>
                ))}
                {closed && closed.items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-[12.5px] text-muted-foreground">
                      {t("Здесь пусто.")}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function StageChip({ stage }: { stage: Deal["stage"] }) {
  if (!stage) return <span>—</span>;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[11.5px] font-semibold">
      <span className="size-2 rounded-full" style={{ background: hex(stage.color) }} aria-hidden />
      {stage.name}
    </span>
  );
}

/* ---------------------------------------------------------------- новая сделка */

function NewDeal({ draft, pipelines, pipelineId, onCancel, onCreated, flash }: { draft: DealInput; pipelines: Pipeline[]; pipelineId: string; onCancel: () => void; onCreated: (d: Deal) => void; flash: (t: string) => void }) {
  const [form, setForm] = useState<DealInput>({ title: "", currency: "AED", pipelineId: draft.pipelineId || pipelineId, ...draft });
  const [contact, setContact] = useState<Contact | null>(null);
  const [busy, setBusy] = useState(false);
  const pipeline = pipelines.find((p) => p.id === form.pipelineId) ?? pipelines[0];
  const stages = pipeline?.stages.filter((s) => s.kind === "open") ?? [];

  useEffect(() => {
    if (draft.contactId) contactsApi.get(draft.contactId).then(setContact, () => undefined);
  }, [draft.contactId]);
  useEffect(() => {
    if (contact && !form.title) setForm((f) => ({ ...f, title: t("Сделка с {name}", { name: contact.name }) }));
  }, [contact, form.title]);

  const submit = async () => {
    setBusy(true);
    try {
      onCreated(await dealsApi.create({ ...form, stageId: form.stageId || stages[0]?.id }));
      flash(t("Сделка создана"));
    } catch (e) {
      flash(apiErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="p-3.5 md:p-5"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <button type="button" onClick={onCancel} className="inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground">
        <i className="ri-arrow-left-line leading-none" aria-hidden />
        {t("К сделкам")}
      </button>
      <h3 className="mt-3 text-base font-semibold">{t("Новая сделка")}</h3>
      <div className="mt-3 grid max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
        <label className="block sm:col-span-2">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Название")}</span>
          <input autoFocus value={form.title ?? ""} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required maxLength={300} className={cn(input, "mt-1")} />
        </label>
        <label className="block">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Воронка")}</span>
          <select value={form.pipelineId ?? ""} onChange={(e) => setForm((f) => ({ ...f, pipelineId: e.target.value, stageId: undefined }))} className={cn(input, "mt-1")}>
            {pipelines.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Этап")}</span>
          <select value={form.stageId ?? stages[0]?.id ?? ""} onChange={(e) => setForm((f) => ({ ...f, stageId: e.target.value }))} className={cn(input, "mt-1")}>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Сумма")}</span>
          <input type="number" min={0} step="0.01" value={form.amount ?? ""} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value === "" ? null : Number(e.target.value) }))} className={cn(input, "mt-1")} />
        </label>
        <label className="block">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Валюта")}</span>
          <input value={form.currency ?? "AED"} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} maxLength={3} className={cn(input, "mt-1")} />
        </label>
        <div className="sm:col-span-2">
          <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Клиент")}</span>
          <ContactPick value={contact} onPick={(c) => { setContact(c); setForm((f) => ({ ...f, contactId: c?.id ?? null })); }} />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button type="submit" disabled={busy || !(form.title ?? "").trim()} className={primary}>
          {busy ? t("Сохраняю…") : t("Создать сделку")}
        </button>
        <button type="button" onClick={onCancel} className={btn}>
          {t("Отмена")}
        </button>
      </div>
    </form>
  );
}

function ContactPick({ value, onPick }: { value: Contact | null; onPick: (c: Contact | null) => void }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Contact[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) {
      setRows([]);
      return;
    }
    const h = setTimeout(() => contactsApi.list({ q, limit: 8 }).then((r) => setRows(r.items), () => undefined), 250);
    return () => clearTimeout(h);
  }, [q]);
  if (value) {
    return (
      <p className="mt-1 flex items-center gap-2 text-[12.5px]">
        <i className="ri-user-line text-muted-foreground" aria-hidden />
        <span className="font-semibold">{value.name}</span>
        <span className="text-muted-foreground">{value.phone || value.email || ""}</span>
        <button type="button" onClick={() => onPick(null)} className="cursor-pointer text-[11.5px] text-muted-foreground hover:text-foreground">
          {t("сменить")}
        </button>
      </p>
    );
  }
  return (
    <div className="relative mt-1">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Имя, телефон или почта контакта")} aria-label={t("Найти контакт")} className={input} />
      {rows.length > 0 ? (
        <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-64 overflow-auto rounded-btn border border-border bg-card shadow-lg">
          {rows.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => { setQ(""); onPick(c); }} className="flex w-full cursor-pointer flex-col px-3 py-2 text-start transition hover:bg-muted">
                <span className="text-[12.5px] font-semibold">{c.name}</span>
                <span className="text-[11.5px] text-muted-foreground">{[c.phone, c.company].filter(Boolean).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function CompanyPick({ onPick }: { onPick: (c: Company) => void }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Company[]>([]);
  useEffect(() => {
    if (q.trim().length < 2) {
      setRows([]);
      return;
    }
    const h = setTimeout(() => companiesApi.list({ q, size: 8 }).then((r) => setRows(r.items), () => undefined), 250);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div className="relative mt-1">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Название компании")} aria-label={t("Найти компанию")} className={input} />
      {rows.length > 0 ? (
        <ul className="absolute inset-x-0 top-full z-10 mt-1 max-h-64 overflow-auto rounded-btn border border-border bg-card shadow-lg">
          {rows.map((c) => (
            <li key={c.id}>
              <button type="button" onClick={() => { setQ(""); onPick(c); }} className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-start text-[12.5px] transition hover:bg-muted">
                <span className="min-w-0 flex-1 truncate font-semibold">{c.name}</span>
                <span className="text-[11.5px] text-muted-foreground">{c.country}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- карточка сделки */

const TEXT_FIELDS: { key: keyof DealInput; label: string; wide?: boolean; rows?: number }[] = [
  { key: "source", label: t("Источник") },
  { key: "closingDate", label: t("Ожидаемое закрытие") },
  { key: "nextStep", label: t("Следующий шаг"), wide: true },
  { key: "description", label: t("Описание"), wide: true, rows: 4 },
];

function DealCard({ id, pipelines, owners, staff, onBack, onChanged, flash }: { id: string; pipelines: Pipeline[]; owners: Owner[]; staff: boolean; onBack: () => void; onChanged: () => void; flash: (t: string) => void }) {
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [d, setD] = useState<Deal | null>(null);
  const [form, setForm] = useState<DealInput>({});
  const [saving, setSaving] = useState(false);
  const [pickContact, setPickContact] = useState(false);
  const [pickCompany, setPickCompany] = useState(false);

  const snapshot = (x: Deal): DealInput => ({
    title: x.title,
    amount: x.amount,
    currency: x.currency,
    source: x.source,
    closingDate: x.closingDate ?? "",
    nextStep: x.nextStep,
    description: x.description,
    lostReason: x.lostReason,
    ownerId: x.ownerId,
  });
  const load = useCallback(async () => {
    try {
      const x = await dealsApi.get(id);
      setD(x);
      setForm(snapshot(x));
    } catch (e) {
      flash(apiErrorText(e));
    }
  }, [id, flash]);
  useEffect(() => {
    void load();
  }, [load]);

  const stages = useMemo(() => pipelines.flatMap((p) => p.stages.map((s) => ({ ...s, pipeline: p.name, many: pipelines.length > 1 }))), [pipelines]);

  if (!d) return <p className="p-5 text-[12.5px] text-muted-foreground">{t("Загрузка…")}</p>;
  const base = snapshot(d);
  const dirty = (Object.keys(form) as (keyof DealInput)[]).some((k) => (form[k] ?? "") !== (base[k] ?? ""));

  const act = async (fn: () => Promise<unknown>, text: string) => {
    try {
      await fn();
      await load();
      onChanged();
      flash(text);
    } catch (e) {
      flash(apiErrorText(e));
    }
  };
  const save = async () => {
    setSaving(true);
    try {
      const changed = Object.fromEntries((Object.keys(form) as (keyof DealInput)[]).filter((k) => (form[k] ?? "") !== (base[k] ?? "")).map((k) => [k, form[k] === "" && k === "closingDate" ? null : form[k]]));
      await dealsApi.update(id, changed);
      await load();
      onChanged();
      flash(t("Сделка сохранена"));
    } catch (e) {
      flash(apiErrorText(e));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(t("Удалить сделку «{title}»? История этапов тоже удалится.", { title: d.title }))) return;
    try {
      await dealsApi.remove(id);
      onChanged();
      flash(t("Сделка удалена"));
      onBack();
    } catch (e) {
      flash(apiErrorText(e));
    }
  };

  return (
    <div className="p-3.5 md:p-5">
      <button type="button" onClick={onBack} className="inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground">
        <i className="ri-arrow-left-line leading-none" aria-hidden />
        {t("К сделкам")}
      </button>
      <div className="mt-3 flex flex-wrap items-start gap-2.5">
        <span className="grid size-9 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
          <i className="ri-briefcase-line text-lg leading-none" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold leading-snug">{d.title}</h3>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
            <StageChip stage={d.stage} />
            <span>{d.stage ? KIND_LABEL[d.stage.kind] : ""}</span>
            <span>· {t("{n} на этапе", { n: days(d.daysInStage) })}</span>
            {d.zcrmUrl ? (
              <a href={d.zcrmUrl} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                · {t("открыть в Zoho CRM")}
              </a>
            ) : (
              <span>· {t("заведена в Hub")}</span>
            )}
          </p>
        </div>
        {!readOnly ? (
          <select
            value={d.stageId}
            onChange={(e) => void act(() => dealsApi.update(id, { stageId: e.target.value }), t("Этап изменён"))}
            aria-label={t("Этап сделки")}
            className={cn(input, "w-auto max-w-xs")}
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.many ? `${s.pipeline}: ${s.name}` : s.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <section aria-label={t("Данные сделки")}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block sm:col-span-2">
              <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Название")}</span>
              <input value={form.title ?? ""} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} maxLength={300} className={cn(input, "mt-1")} />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Сумма")}</span>
              <input type="number" min={0} step="0.01" value={form.amount ?? ""} disabled={readOnly} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value === "" ? null : Number(e.target.value) }))} className={cn(input, "mt-1")} />
            </label>
            <label className="block">
              <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Валюта")}</span>
              <input value={form.currency ?? ""} disabled={readOnly} maxLength={3} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))} className={cn(input, "mt-1")} />
            </label>
            <label className="block sm:col-span-2">
              <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Ответственный")}</span>
              <select value={form.ownerId ?? ""} disabled={readOnly || !staff} onChange={(e) => setForm((f) => ({ ...f, ownerId: e.target.value || null }))} className={cn(input, "mt-1")}>
                <option value="">{d.salesperson ? t("не назначен · продавец Zoho: {name}", { name: d.salesperson }) : t("не назначен")}</option>
                {owners.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} · {o.role}
                  </option>
                ))}
              </select>
            </label>
            {TEXT_FIELDS.map((f) => (
              <label key={f.key} className={cn("block", f.wide && "sm:col-span-2")}>
                <span className="text-[11.5px] font-semibold text-muted-foreground">{f.label}</span>
                {f.rows ? (
                  <textarea value={(form[f.key] as string) ?? ""} disabled={readOnly} rows={f.rows} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))} className={cn(input, "mt-1 h-auto py-2")} />
                ) : (
                  <input type={f.key === "closingDate" ? "date" : "text"} value={(form[f.key] as string) ?? ""} disabled={readOnly} onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))} className={cn(input, "mt-1")} />
                )}
              </label>
            ))}
            {d.stage?.kind === "lost" ? (
              <label className="block sm:col-span-2">
                <span className="text-[11.5px] font-semibold text-muted-foreground">{t("Причина проигрыша")}</span>
                <input value={form.lostReason ?? ""} disabled={readOnly} maxLength={200} onChange={(e) => setForm((f) => ({ ...f, lostReason: e.target.value }))} className={cn(input, "mt-1")} />
              </label>
            ) : null}
          </div>
          {describeAds(d.utm).length ? (
            <div className="mt-2 flex flex-col gap-0.5 text-[11.5px] text-muted-foreground">
              {describeAds(d.utm).map((l) => (
                <p key={l} className="flex gap-1.5">
                  <i className="ri-megaphone-line mt-0.5" aria-hidden />
                  <span>{l}</span>
                </p>
              ))}
            </div>
          ) : null}
          {!readOnly ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" disabled={!dirty || saving} onClick={() => void save()} className={primary}>
                {saving ? t("Сохраняю…") : t("Сохранить")}
              </button>
              {staff || d.ownerId === user?.id ? (
                <button type="button" onClick={() => void remove()} className={cn(btn, "text-red-600")}>
                  {t("Удалить")}
                </button>
              ) : null}
            </div>
          ) : null}
        </section>

        <section aria-label={t("Клиент и история")}>
          <h4 className="text-[13px] font-semibold">{t("Клиент")}</h4>
          <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
            <li className="flex flex-wrap items-center gap-2 px-3 py-2">
              <i className="ri-user-line text-muted-foreground" aria-hidden />
              {d.contact ? (
                <button
                  type="button"
                  onClick={() => {
                    showContact(d.contact!.id);
                    goAnchor("#contacts-app");
                  }}
                  className="min-w-0 flex-1 cursor-pointer text-start text-[12.5px] font-semibold text-primary hover:underline"
                >
                  {d.contact.name}
                  <span className="ms-2 font-normal text-muted-foreground">{d.contact.phone || d.contact.email || ""}</span>
                </button>
              ) : (
                <span className="flex-1 text-[12.5px] text-muted-foreground">{t("Контакт не указан")}</span>
              )}
              {!readOnly ? (
                <button type="button" onClick={() => setPickContact((v) => !v)} className="cursor-pointer text-[11.5px] font-semibold text-muted-foreground hover:text-foreground">
                  {d.contact ? t("сменить") : t("выбрать")}
                </button>
              ) : null}
            </li>
            {pickContact ? (
              <li className="px-3 py-2">
                <ContactPick value={null} onPick={(c) => { setPickContact(false); void act(() => dealsApi.update(id, { contactId: c?.id ?? null }), t("Контакт сделки изменён")); }} />
              </li>
            ) : null}
            <li className="flex flex-wrap items-center gap-2 px-3 py-2">
              <i className="ri-building-2-line text-muted-foreground" aria-hidden />
              {d.company ? (
                <button
                  type="button"
                  onClick={() => {
                    showCompany(d.company!.id);
                    goAnchor("#companies");
                  }}
                  className="min-w-0 flex-1 cursor-pointer text-start text-[12.5px] font-semibold text-primary hover:underline"
                >
                  {d.company.name}
                </button>
              ) : (
                <span className="flex-1 text-[12.5px] text-muted-foreground">{t("Компания не указана")}</span>
              )}
              {!readOnly ? (
                <button type="button" onClick={() => setPickCompany((v) => !v)} className="cursor-pointer text-[11.5px] font-semibold text-muted-foreground hover:text-foreground">
                  {d.company ? t("сменить") : t("выбрать")}
                </button>
              ) : null}
            </li>
            {pickCompany ? (
              <li className="px-3 py-2">
                <CompanyPick onPick={(c) => { setPickCompany(false); void act(() => dealsApi.update(id, { companyId: c.id }), t("Компания сделки изменена")); }} />
              </li>
            ) : null}
          </ul>

          <BooksDocs docs={d.orders ?? []} />

          <div className="mt-4 flex flex-col gap-2">
            <TasksBlock parent={{ dealId: d.id }} onFlash={flash} />
            <NotesBlock parent={{ dealId: d.id }} onFlash={flash} />
          </div>

          <h4 className="mt-5 text-[13px] font-semibold">{t("История этапов")}</h4>
          <ol className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
            {(d.history ?? []).map((h, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12px]">
                <span className="tabular-nums text-muted-foreground">{when(h.at)}</span>
                <span className="font-semibold">{h.from ? `${h.from} → ${h.to}` : h.to}</span>
                {h.by ? <span className="text-muted-foreground">· {h.by}</span> : null}
              </li>
            ))}
            {(d.history ?? []).length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("Пока нет записей.")}</li> : null}
          </ol>
          <p className="mt-2 text-[11.5px] text-muted-foreground">
            {t("Создана {when}", { when: when(d.createdAt) })}
            {d.closedAt ? ` · ${t("закрыта {when}", { when: when(d.closedAt) })}` : ""}
          </p>
        </section>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- заказы и счета Zoho Books по сделке */

function BooksDocs({ docs }: { docs: Deal["orders"] & object }) {
  const billed = docs.filter((o) => o.kind === "invoice" && o.status !== "draft" && o.status !== "void");
  const total = billed.reduce((s, o) => s + o.total, 0);
  const due = billed.reduce((s, o) => s + o.balance, 0);
  const paid = docs.filter((o) => o.kind === "payment").reduce((s, o) => s + o.total, 0);
  const cur = docs[0]?.currency ?? "AED";
  return (
    <>
      <h4 className="mt-5 text-[13px] font-semibold">
        {t("Заказы и счета Books")}
        {docs.length ? ` · ${docs.length}` : ""}
      </h4>
      {billed.length ? (
        <p className="mt-1 text-[11.5px] text-muted-foreground">
          {t("Счетов на {sum}", { sum: money(total, cur) })}
          {paid > 0.5 ? ` · ${t("оплачено {sum}", { sum: money(paid, cur) })}` : ""}
          {due > 0.5 ? ` · ${t("к оплате {sum}", { sum: money(due, cur) })}` : ` · ${t("оплачено полностью")}`}
        </p>
      ) : null}
      <ul className="mt-2 flex flex-col divide-y divide-border/60 rounded-btn border border-border">
        {docs.map((o) => (
          <DocRow key={o.id} o={o} />
        ))}
        {docs.length === 0 ? <li className="px-3 py-3 text-[12px] text-muted-foreground">{t("В Books нет заказов по этой сделке.")}</li> : null}
      </ul>
    </>
  );
}

/* ---------------------------------------------------------------- этапы воронки (владелец и администраторы) */

function StageEditor({ pipeline, pipelines, onClose, onChanged, flash }: { pipeline: Pipeline; pipelines: Pipeline[]; onClose: () => void; onChanged: () => Promise<unknown>; flash: (t: string) => void }) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<StageKind>("open");
  const [names, setNames] = useState<Record<string, string>>({});
  const [pipeName, setPipeName] = useState(pipeline.name);
  const [newPipe, setNewPipe] = useState("");

  const act = async (fn: () => Promise<unknown>, text: string) => {
    try {
      await fn();
      await onChanged();
      flash(text);
    } catch (e) {
      flash(apiErrorText(e));
    }
  };
  const stages = pipeline.stages;
  const move = (s: DealStage, dir: -1 | 1) => {
    const i = stages.findIndex((x) => x.id === s.id);
    const j = i + dir;
    if (j < 0 || j >= stages.length) return;
    // после кого встать: при подъёме — после того, кто стоит перед целью; при спуске — после цели
    const afterId = dir === -1 ? (stages[j - 1]?.id ?? "") : stages[j].id;
    void act(() => dealsApi.updateStage(s.id, { afterId }), t("Порядок этапов сохранён"));
  };

  return (
    <div className="p-3.5 md:p-5">
      <button type="button" onClick={onClose} className="inline-flex cursor-pointer items-center gap-1 text-[12px] font-semibold text-muted-foreground transition hover:text-foreground">
        <i className="ri-arrow-left-line leading-none" aria-hidden />
        {t("К сделкам")}
      </button>
      <h3 className="mt-3 text-base font-semibold">{t("Этапы воронки «{name}»", { name: pipeline.name })}</h3>
      <p className="mt-1 text-[12px] text-muted-foreground">
        {t("Названия и порядок — как на канбане. «Выиграна» и «проиграна» закрывают сделку. Этапы из Zoho CRM остаются связанными с CRM по исходному названию, переименование это не ломает.")}
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={pipeName} onChange={(e) => setPipeName(e.target.value)} maxLength={60} aria-label={t("Название воронки")} className={cn(input, "max-w-xs")} />
        <button type="button" disabled={pipeName.trim() === pipeline.name || !pipeName.trim()} onClick={() => void act(() => dealsApi.updatePipeline(pipeline.id, { name: pipeName.trim() }), t("Воронка переименована"))} className={btn}>
          {t("Переименовать")}
        </button>
        {!pipeline.isDefault ? (
          <button type="button" onClick={() => void act(() => dealsApi.updatePipeline(pipeline.id, { isDefault: true }), t("Теперь это воронка по умолчанию"))} className={btn}>
            {t("Сделать основной")}
          </button>
        ) : (
          <span className="text-[12px] text-muted-foreground">{t("воронка по умолчанию")}</span>
        )}
        {!pipeline.isDefault && pipelines.length > 1 ? (
          <button type="button" onClick={() => void act(() => dealsApi.removePipeline(pipeline.id).then(onClose), t("Воронка удалена"))} className={cn(btn, "text-red-600")}>
            {t("Удалить воронку")}
          </button>
        ) : null}
      </div>
      <ul className="mt-4 flex max-w-3xl flex-col divide-y divide-border/60 rounded-btn border border-border">
        {stages.map((s, i) => (
          <li key={s.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
            <span className="size-2.5 flex-none rounded-full" style={{ background: hex(s.color) }} aria-hidden />
            <input
              value={names[s.id] ?? s.name}
              onChange={(e) => setNames((n) => ({ ...n, [s.id]: e.target.value }))}
              onBlur={() => {
                const v = (names[s.id] ?? s.name).trim();
                if (v && v !== s.name) void act(() => dealsApi.updateStage(s.id, { name: v }), t("Этап переименован"));
              }}
              maxLength={60}
              aria-label={t("Название этапа")}
              className={cn(input, "min-w-[160px] flex-1")}
            />
            <select value={s.kind} onChange={(e) => void act(() => dealsApi.updateStage(s.id, { kind: e.target.value as StageKind }), t("Тип этапа изменён"))} aria-label={t("Тип этапа")} className={cn(input, "w-auto")}>
              {(Object.keys(KIND_LABEL) as StageKind[]).map((k) => (
                <option key={k} value={k}>
                  {KIND_LABEL[k]}
                </option>
              ))}
            </select>
            <select value={s.color} onChange={(e) => void act(() => dealsApi.updateStage(s.id, { color: e.target.value }), t("Цвет этапа изменён"))} aria-label={t("Цвет этапа")} className={cn(input, "w-auto")}>
              {tagColors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <span className="w-16 text-end text-[11.5px] tabular-nums text-muted-foreground">{t("{n} сд.", { n: s.count })}</span>
            <button type="button" disabled={i === 0} onClick={() => move(s, -1)} aria-label={t("Выше")} className="grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground hover:bg-muted disabled:opacity-30">
              <i className="ri-arrow-up-line" aria-hidden />
            </button>
            <button type="button" disabled={i === stages.length - 1} onClick={() => move(s, 1)} aria-label={t("Ниже")} className="grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground hover:bg-muted disabled:opacity-30">
              <i className="ri-arrow-down-line" aria-hidden />
            </button>
            <button
              type="button"
              disabled={s.count > 0 || stages.length === 1}
              title={s.count > 0 ? t("На этапе есть сделки — сначала перенесите их") : undefined}
              onClick={() => void act(() => dealsApi.removeStage(s.id), t("Этап удалён"))}
              aria-label={t("Удалить этап")}
              className="grid size-8 cursor-pointer place-items-center rounded-btn text-muted-foreground hover:bg-muted hover:text-red-600 disabled:opacity-30"
            >
              <i className="ri-delete-bin-line" aria-hidden />
            </button>
          </li>
        ))}
      </ul>
      <form
        className="mt-3 flex max-w-3xl flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          void act(() => dealsApi.createStage(pipeline.id, { name: name.trim(), kind, afterId: stages[stages.length - 1]?.id ?? "" }).then(() => setName("")), t("Этап добавлен"));
        }}
      >
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder={t("Новый этап")} aria-label={t("Название нового этапа")} className={cn(input, "max-w-xs")} />
        <select value={kind} onChange={(e) => setKind(e.target.value as StageKind)} aria-label={t("Тип нового этапа")} className={cn(input, "w-auto")}>
          {(Object.keys(KIND_LABEL) as StageKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
        <button type="submit" disabled={!name.trim()} className={primary}>
          {t("Добавить этап")}
        </button>
      </form>
      <form
        className="mt-5 flex max-w-3xl flex-wrap items-center gap-2 border-t border-border pt-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!newPipe.trim()) return;
          void act(() => dealsApi.createPipeline(newPipe.trim()).then(() => setNewPipe("")), t("Воронка создана — выберите её в списке над канбаном"));
        }}
      >
        <input value={newPipe} onChange={(e) => setNewPipe(e.target.value)} maxLength={60} placeholder={t("Новая воронка")} aria-label={t("Название новой воронки")} className={cn(input, "max-w-xs")} />
        <button type="submit" disabled={!newPipe.trim()} className={btn}>
          {t("Создать воронку")}
        </button>
      </form>
    </div>
  );
}

export default DealsDemo;
